import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { usePageTitle } from "@/lib/usePageTitle";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OfferBadge } from "@/components/OfferBadge";
import { rupees, toMinor } from "@/lib/format";
import { addressLabel } from "@/lib/addressLabel";
import { useRequireAuth } from "@/lib/useRequireAuth";

export default function Cart() {
  const { t, lang, cart, refreshCart, fulfillmentPrefill, setFulfillmentPrefill } = useApp();
  usePageTitle(t("commerce.cart")); // R4-7: per-page tab title
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [form, setForm] = useState({ fulfillment_type: "delivery", address: "", landmark: "", notes: "", payment_method: "cash" });
  // D2 (storefront S3): "To my room" only asks a room number (hotel handoff) — it rides in
  // `notes`, same as the salon home-service address does in `DoctorSection.jsx`, rather than a
  // new order field. Kept as its own bit of state so the plain notes textarea stays untouched
  // until submit.
  const [roomNumber, setRoomNumber] = useState("");
  const [points, setPoints] = useState(0);
  const [busy, setBusy] = useState(false);
  const [orderError, setOrderError] = useState("");
  // UAT follow-on fix (C1 "UPI pay dead-end"): the public business endpoint never sends `upi_id`
  // (owner-only) — it sends the public boolean `accepts_upi`. This was reading `upi_id`, always
  // "", so the cash/UPI selector below was permanently hidden for every citizen.
  const [businessAcceptsUpi, setBusinessAcceptsUpi] = useState(false);
  // PR4 (design_handoff_business_websites): a `stay_booking` business (a hotel) can take an
  // order for delivery to a guest's room, with no room/booking id attached — same as pickup
  // needs no address. Read off the same business-info fetch already made for `accepts_upi`.
  const [businessCapabilities, setBusinessCapabilities] = useState([]);
  const [savedAddresses, setSavedAddresses] = useState([]);
  // R6-8: a shop that has turned online orders OFF (a Free-plan shop - the server's
  // `ordering_not_enabled` gate) must be flagged on the cart itself, not only when Place order fails.
  const [ordersOff, setOrdersOff] = useState(false);

  const reward = cart?.reward;
  // Points preview, in PAISE (minor units) to match `cart.totals.*`. `points / points_per_rupee`
  // is rupees; ×100 makes paise. Display only — checkout sends `points_to_use`, not this.
  const discount = reward && points ? toMinor(points / reward.points_per_rupee) : 0;
  const canRoomService = businessCapabilities.includes("stay_booking");

  useEffect(() => {
    if (!cart?.business_id) return;
    api.get(`/directory/businesses/${cart.business_id}`).then((r) => {
      setBusinessAcceptsUpi(!!r.data.accepts_upi);
      setBusinessCapabilities(r.data.capabilities || []);
      setOrdersOff(r.data.plan === "free");
    });
  }, [cart?.business_id]);

  useEffect(() => {
    api
      .get("/addresses")
      .then((r) => {
        const list = rows(r.data);
        setSavedAddresses(list);
        // Prefill the default (else the first) saved address so the chip that looks selected is
        // actually in the field. Never overwrites something the citizen has already typed.
        const pick = list.find((a) => a.is_default) || list[0];
        if (pick) {
          setForm((prev) => (prev.address ? prev : { ...prev, address: pick.address, landmark: prev.landmark || pick.landmark || "" }));
        }
      })
      .catch(() => undefined);
  }, []);

  // D2 (storefront S3): consume the one-shot fulfilment-mode prefill set by the hotel Dining
  // section's chips (`MenuSection.jsx`) before it navigated here — same lifecycle as
  // `enquiryPrefill`/`EnquirySection`: reacts to the value (not just on mount, since Cart is
  // usually already mounted from an earlier cart open), then clears it.
  useEffect(() => {
    if (!fulfillmentPrefill) return;
    setForm((prev) => ({ ...prev, fulfillment_type: fulfillmentPrefill.fulfillment_type || prev.fulfillment_type }));
    if (fulfillmentPrefill.room_number !== undefined) setRoomNumber(fulfillmentPrefill.room_number);
    setFulfillmentPrefill(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fulfillmentPrefill]);

  const setQuantity = async (item, quantity) => {
    try {
      await api.patch(`/cart/items/${item.id}`, { quantity });
      await refreshCart();
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    }
  };

  const clear = async () => {
    await api.post("/cart/clear");
    await refreshCart();
  };

  const placeOrder = async () => {
    // Before the network call (L2): a signed-out guest gets the sign-in prompt, no POST, and the
    // form they filled in stays put.
    if (!requireAuth()) return;
    setBusy(true);
    setOrderError("");
    try {
      const notes =
        form.fulfillment_type === "room_service" && roomNumber.trim()
          ? [`${t("commerce.roomNumberPlaceholder")}: ${roomNumber.trim()}`, form.notes.trim()].filter(Boolean).join(" — ")
          : form.notes;
      const { data } = await api.post("/orders", { ...form, notes, points_to_use: points });
      await refreshCart();
      toast.success(t("commerce.orderPlaced"));
      navigate(`/activity?order=${data.id}`);
    } catch (e) {
      // The toast stays (it is how every other action here reports), but a checkout failure also
      // needs to persist NEXT TO the button: the toast is gone in seconds while the filled form
      // is still on screen, which leaves "did my order go through?" unanswered at the one moment
      // that question costs money. Double-submit was already guarded by `disabled={busy}`.
      const message = apiErrorMessage(e, t("errors.generic"));
      setOrderError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  if (!cart || cart.items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8" data-testid="cart-empty">
        <h1 className="font-heading text-3xl font-bold tracking-tight">{t("commerce.cart")}</h1>
        <p className="mt-4 text-muted-foreground">{t("commerce.cartEmpty")}</p>
        <Button className="mt-6 rounded-full" data-testid="cart-browse-btn" onClick={() => navigate("/directory")}>
          {t("home.exploreBusinesses")}
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="cart-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight">{t("commerce.cart")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("commerce.cartFrom")}: <span className="font-medium text-foreground">{cart.business_name}</span>
      </p>
      {cart.offer && (
        <div className="mt-4" data-testid="cart-offer-badge">
          <OfferBadge offer={cart.offer} />
          <span className="ml-2 text-sm text-muted-foreground">
            {t("offers.appliedInCart")} · {localized(lang, cart.offer, "title")}
          </span>
        </div>
      )}

      <div className="mt-6 divide-y divide-border overflow-hidden rounded-2xl border border-border">
        {cart.items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 p-4" data-testid={`cart-item-${item.id}`}>
            <div className="flex-1">
              <p className="font-medium">{localized(lang, item, "name")}</p>
              {item.option_name && (
                <p className="text-xs text-muted-foreground" data-testid={`cart-item-option-${item.id}`}>
                  {lang === "hi" ? item.option_name_hi || item.option_name : item.option_name}
                </p>
              )}
              <p className="tabular-nums text-sm text-muted-foreground">{rupees({ minor_units: item.unit_price_minor_units })}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 rounded-full"
                data-testid={`cart-decrease-${item.id}`}
                onClick={() => setQuantity(item, item.quantity - 1)}
              >
                <Minus size={13} />
              </Button>
              <span className="w-6 text-center tabular-nums" data-testid={`cart-qty-${item.id}`}>
                {item.quantity}
              </span>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 rounded-full"
                data-testid={`cart-increase-${item.id}`}
                onClick={() => setQuantity(item, item.quantity + 1)}
              >
                <Plus size={13} />
              </Button>
            </div>
            <p className="w-20 text-right tabular-nums">{rupees({ minor_units: item.unit_price_minor_units * item.quantity })}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-2 rounded-2xl border border-border p-5" data-testid="cart-summary">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t("offers.cartSubtotal")}</span>
          <span className="tabular-nums">{rupees({ minor_units: cart.totals.subtotal })}</span>
        </div>
        {cart.offer && (
          <div className="flex items-center justify-between text-sm" data-testid="cart-offer-line">
            <span className="text-[hsl(var(--accent))]">
              {t("offers.cartOfferDiscount")} · {localized(lang, cart.offer, "title")}
            </span>
            <span className="tabular-nums text-[hsl(var(--accent))]">−{rupees({ minor_units: cart.totals.offer_discount })}</span>
          </div>
        )}
        {discount > 0 && (
          <div className="flex items-center justify-between text-sm" data-testid="cart-points-line">
            <span className="text-muted-foreground">{t("commerce.pointsDiscount")}</span>
            <span className="tabular-nums">−{rupees({ minor_units: discount })}</span>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-border pt-2">
          <button className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="cart-clear-btn" onClick={clear}>
            <Trash2 size={14} /> {t("commerce.clearCart")}
          </button>
          <p className="font-heading text-xl font-semibold" data-testid="cart-total">
            {t("commerce.total")}: {rupees({ minor_units: cart.totals.total - discount })}
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-4 rounded-2xl border border-border p-6">
        <h2 className="font-heading text-lg font-semibold">{t("commerce.checkout")}</h2>
        <div className="flex gap-2">
          {[
            ["delivery", t("commerce.deliveryMode")],
            ["pickup", t("commerce.pickupMode")],
            ...(canRoomService ? [["room_service", t("commerce.roomServiceMode")]] : []),
          ].map(([value, label]) => (
            <button
              key={value}
              data-testid={`fulfillment-${value}`}
              onClick={() => setForm({ ...form, fulfillment_type: value })}
              className={`rounded-full border px-4 py-1.5 text-sm ${
                form.fulfillment_type === value
                  ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                  : "border-border"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {form.fulfillment_type === "delivery" && (
          <div className="space-y-3">
            {savedAddresses.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="cart-saved-address-picker">
                {savedAddresses.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`cart-saved-address-${item.id}`}
                    onClick={() => setForm({ ...form, address: item.address, landmark: item.landmark })}
                    aria-pressed={form.address === item.address}
                    className={`rounded-full border px-3 py-1 text-xs ${
                      form.address === item.address ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground" : "border-border"
                    }`}
                  >
                    {addressLabel(item.label, t)}
                  </button>
                ))}
              </div>
            )}
            <Label htmlFor="address">{t("commerce.address")}</Label>
            <Input
              id="address"
              data-testid="order-address-input"
              value={form.address}
              placeholder={t("commerce.addressPlaceholder")}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
            <Label htmlFor="address-landmark">{t("addresses.landmarkField")}</Label>
            <Input
              id="address-landmark"
              data-testid="order-landmark-input"
              value={form.landmark}
              placeholder={t("addresses.landmarkField")}
              onChange={(e) => setForm({ ...form, landmark: e.target.value })}
            />
          </div>
        )}

        {form.fulfillment_type === "room_service" && (
          <div className="space-y-1.5">
            <Label htmlFor="room-number">{t("commerce.roomNumberPlaceholder")}</Label>
            <Input
              id="room-number"
              data-testid="order-room-number-input"
              value={roomNumber}
              placeholder={t("commerce.roomNumberPlaceholder")}
              onChange={(e) => setRoomNumber(e.target.value)}
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="order-notes">{t("commerce.notes")}</Label>
          <Textarea
            id="order-notes"
            rows={2}
            data-testid="order-notes-input"
            value={form.notes}
            placeholder={t("commerce.notesPlaceholder")}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        {businessAcceptsUpi && (
          <div className="flex gap-2">
            {[
              ["cash", t("payment.cashMode")],
              ["upi", t("payment.upiMode")],
            ].map(([value, label]) => (
              <button
                key={value}
                data-testid={`payment-method-${value}`}
                onClick={() => setForm({ ...form, payment_method: value })}
                className={`rounded-full border px-4 py-1.5 text-sm ${
                  form.payment_method === value
                    ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                    : "border-border"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {form.payment_method === "upi" ? t("payment.payAfterPlacing") : t("commerce.payAtDelivery")}
        </p>

        {reward && reward.max_points_usable > 0 && (
          <div className="space-y-2 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 p-4" data-testid="reward-points-box">
            <p className="text-sm font-medium">{t("commerce.usePoints")}</p>
            <p className="text-xs text-muted-foreground">
              {t("commerce.pointsAvailable", { count: reward.balance })} ·{" "}
              {t("commerce.pointsHint", { points: reward.points_per_rupee, percent: reward.max_discount_percent })}
            </p>
            <div className="flex items-center gap-3">
              <Input
                type="number"
                min={0}
                max={reward.max_points_usable}
                data-testid="points-input"
                className="max-w-32"
                value={points}
                onChange={(e) =>
                  setPoints(Math.max(0, Math.min(Number(e.target.value) || 0, reward.max_points_usable)))
                }
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="apply-max-points-btn"
                onClick={() => setPoints(reward.max_points_usable)}
              >
                {t("commerce.applyPoints")}
              </Button>
            </div>
            {discount > 0 && (
              <p className="tabular-nums text-sm" data-testid="points-discount">
                {t("commerce.pointsDiscount")}: −{rupees({ minor_units: discount })} · {t("commerce.total")}:{" "}
                {rupees({ minor_units: cart.totals.total - discount })}
              </p>
            )}
          </div>
        )}

        {ordersOff && (
          <p
            className="rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            role="alert"
            data-testid="cart-orders-off"
          >
            {t("commerce.ordersOff", { shop: cart.business_name })}
          </p>
        )}

        {orderError && (
          <p className="text-sm text-destructive" data-testid="place-order-error">
            {orderError}
          </p>
        )}

        <Button className="rounded-full" data-testid="place-order-btn" disabled={busy || ordersOff} onClick={placeOrder}>
          <ShoppingBag size={15} className="mr-2" />
          {busy ? t("common.saving") : t("commerce.placeOrder")}
        </Button>
      </div>
    </div>
  );
}
